import React, { useEffect, useRef, useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { playDing } from "@/components/pos/OnlineOrderNotifier";
import { curSym } from "../../utils/cur";

// 🔔 Boss / manager bell: a salesman asked to modify a customer's order → approve or reject here.
const TEXTS = {
  zh: { title: "🧳 业务员申请修改订单", open: "个业务员改单待批准", again: "⏰ 业务员改单还没批准", hide: "收起", approve: "✅ 批准", reject: "✖ 拒绝", approved: "已批准，订单已更新", rejected: "已拒绝", add: "新增", remove: "删除", qty: "数量", address: "地址", notes: "备注", customer_name: "客户名", customer_phone: "电话", order: "订单" },
  es: { title: "🧳 Vendedor pide modificar pedido", open: "modificación(es) de vendedor por aprobar", again: "⏰ Modificación de vendedor sin aprobar", hide: "Ocultar", approve: "✅ Aprobar", reject: "✖ Rechazar", approved: "Aprobado, pedido actualizado", rejected: "Rechazado", add: "Agregar", remove: "Quitar", qty: "Cantidad", address: "Dirección", notes: "Notas", customer_name: "Cliente", customer_phone: "Teléfono", order: "Pedido" },
  en: { title: "🧳 Salesman requests order change", open: "salesman change(s) to approve", again: "⏰ Salesman change still pending", hide: "Hide", approve: "✅ Approve", reject: "✖ Reject", approved: "Approved, order updated", rejected: "Rejected", add: "Add", remove: "Remove", qty: "Qty", address: "Address", notes: "Notes", customer_name: "Customer", customer_phone: "Phone", order: "Order" },
  pt: { title: "🧳 Vendedor pede alterar pedido", open: "alteração(ões) por aprovar", again: "⏰ Alteração de vendedor pendente", hide: "Ocultar", approve: "✅ Aprovar", reject: "✖ Rejeitar", approved: "Aprovado, pedido atualizado", rejected: "Rejeitado", add: "Adicionar", remove: "Remover", qty: "Qtd", address: "Endereço", notes: "Notas", customer_name: "Cliente", customer_phone: "Telefone", order: "Pedido" },
};
const cfg = () => TEXTS[localStorage.getItem("app_lang") || "zh"] || TEXTS.zh;

const changeLine = (ch, t) => {
  if (ch.type === "add") return `➕ ${t.add}: ${ch.name || ch.product_id} ×${ch.qty}`;
  if (ch.type === "remove") return `🗑 ${t.remove}: ${ch.name} (×${ch.old_qty})`;
  if (ch.type === "qty") return `✏️ ${ch.name}: ${ch.old_qty} → ${ch.qty}`;
  return `${t[ch.type] || ch.type}: ${ch.old || "—"} → ${ch.new || "—"}`;
};

export default function VendedorChangeNotifier() {
  const [items, setItems] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [busy, setBusy] = useState("");
  const knownRef = useRef(new Set());
  const seededRef = useRef(false);
  const lastRingRef = useRef(0);

  const tick = async () => {
    try {
      const r = await axios.get(`${API}/vendedor/change-requests`, { params: { status: "pending" } });
      const list = Array.isArray(r.data) ? r.data : [];
      setItems(list);
      const t = cfg();
      const now = Date.now();
      const fresh = list.filter((a) => !knownRef.current.has(a.id));
      fresh.forEach((a) => knownRef.current.add(a.id));
      if (fresh.length > 0 && seededRef.current) {
        setCollapsed(false);
        playDing("loud");
        lastRingRef.current = now;
        fresh.forEach((a) => toast.warning(`${t.title}: ${a.salesman_name} · ${a.order_no}`, { duration: 20000 }));
      } else if (fresh.length > 0 && list.length > 0) {
        playDing("crisp");
        lastRingRef.current = now;
      } else if (list.length > 0 && now - lastRingRef.current >= 120000) {
        playDing("crisp");
        lastRingRef.current = now;
        toast.warning(`${t.again} (${list.length})`, { duration: 8000 });
      }
      seededRef.current = true;
    } catch { /* offline — retry next tick */ }
  };
  useEffect(() => {
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);

  const decide = async (a, action) => {
    const t = cfg();
    setBusy(a.id);
    try {
      const r = await axios.post(`${API}/vendedor/change-requests/${a.id}/${action}`);
      if (r.data?.status === "failed") toast.error(r.data?.error || "Error");
      else toast.success(action === "approve" ? t.approved : t.rejected);
      setItems((prev) => prev.filter((x) => x.id !== a.id));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy("");
  };

  if (items.length === 0) return null;
  const t = cfg();
  if (collapsed) {
    return (
      <button onClick={() => setCollapsed(false)} data-testid="vd-change-notify-collapsed"
        className="fixed bottom-52 right-4 z-[900] bg-violet-500 text-white font-black px-4 py-3 rounded-full shadow-2xl animate-pulse">
        🔔 {items.length} {t.open}
      </button>
    );
  }
  return (
    <div className="fixed bottom-52 right-4 z-[900] w-[min(92vw,400px)] bg-violet-700 border-4 border-violet-200 rounded-2xl shadow-2xl text-white overflow-hidden" data-testid="vd-change-notify-panel">
      <div className="flex items-center justify-between px-4 py-2 bg-violet-800">
        <span className="font-black text-sm">{t.title}</span>
        <button onClick={() => setCollapsed(true)} className="text-xs underline opacity-80" data-testid="vd-change-notify-hide">{t.hide}</button>
      </div>
      <div className="max-h-[50vh] overflow-y-auto divide-y divide-violet-400/60">
        {items.map((a) => (
          <div key={a.id} className="px-4 py-2.5 space-y-1.5" data-testid={`vd-change-${a.id}`}>
            <div className="text-sm font-black">{a.salesman_name} · {t.order} {a.order_no}</div>
            <div className="text-xs opacity-90">{a.customer_name} · {curSym()}{Number(a.total_before || 0).toFixed(2)} · {(a.created_at || "").slice(5, 16).replace("T", " ")}</div>
            <ul className="text-xs bg-violet-900/50 rounded-lg px-2.5 py-1.5 space-y-0.5">
              {(a.changes || []).map((ch, i) => <li key={i}>{changeLine(ch, t)}</li>)}
            </ul>
            <div className="flex gap-2">
              <button disabled={busy === a.id} onClick={() => decide(a, "approve")} className="flex-1 bg-white text-violet-800 font-black py-2 rounded-lg hover:bg-violet-50 disabled:opacity-50" data-testid={`vd-change-approve-${a.id}`}>{t.approve}</button>
              <button disabled={busy === a.id} onClick={() => decide(a, "reject")} className="flex-1 bg-violet-900/60 text-white font-bold py-2 rounded-lg hover:bg-violet-900 disabled:opacity-50" data-testid={`vd-change-reject-${a.id}`}>{t.reject}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
